import React from 'react';
import { NavLink } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';


interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

const NAV_ITEMS = [
  { path: '/', label: 'Dashboard', icon: '🏠', exact: true },
  { path: '/explorer', label: 'Paper Explorer', icon: '🔍' },
  { path: '/performance', label: 'Model Performance', icon: '🍩' },
  { path: '/graph', label: 'Network Graph', icon: '🌐' },
  { path: '/about', label: 'About Project', icon: 'ℹ️' },
];

export const Sidebar: React.FC<SidebarProps> = ({ isOpen, onToggle }) => {
  return (
    <>
      <button 
        className="mobile-toggle flex items-center justify-center" 
        onClick={onToggle} 
        aria-label="Toggle menu"
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={isOpen ? "close" : "open"}
            initial={{ opacity: 0, rotate: -90 }}
            animate={{ opacity: 1, rotate: 0 }}
            exit={{ opacity: 0, rotate: 90 }}
            transition={{ duration: 0.2 }}
          >
            {isOpen ? <span className="icon-fallback"  >❌</span> : <span className="icon-fallback"  >☰</span>}
          </motion.div>
        </AnimatePresence>
      </button>

      <aside className={`sidebar flex flex-col ${isOpen ? 'open' : ''}`}>
        <div className="sidebar-brand relative overflow-hidden">
          <div className="absolute inset-0 bg-accent-gradient opacity-10 pointer-events-none" />
          <motion.div 
            className="brand-icon shadow-glow"
            whileHover={{ scale: 1.05, rotate: 5 }}
            transition={{ type: "spring", stiffness: 400, damping: 10 }}
          >
            🧠
          </motion.div>
          <div className="brand-text">
            <h2 style={{ color: '#ffffff' }}>
              GNN Classifier
            </h2>
            <span style={{ color: '#6bf7fb', fontWeight: 700, letterSpacing: '0.1em', fontSize: '0.7rem' }}>PRO</span>
          </div>
        </div>

        <nav className="sidebar-nav mt-4">
          <div className="px-3 mb-2 text-[0.65rem] font-bold text-text-muted uppercase tracking-wider">
            Menu
          </div>
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.exact}
              className={({ isActive }) => `
                flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 relative group
                ${isActive 
                  ? 'bg-[rgba(0,251,251,0.1)] text-text-primary' 
                  : 'text-text-secondary hover:bg-bg-glass-hover hover:text-text-primary'}
              `}
              onClick={() => window.innerWidth < 768 && onToggle()}
            >
              {({ isActive }) => (
                <>
                  <span
                    className={`text-[1.35rem] transition-colors duration-300 ${isActive ? 'text-accent-blue' : 'text-text-muted group-hover:text-accent-cyan'}`} 
                  >
                    {item.icon}
                  </span>
                  <span className={`font-medium ${isActive ? 'font-semibold' : ''}`}>
                    {item.label}
                  </span>
                  
                  {isActive && (
                    <motion.div 
                      layoutId="sidebar-active-indicator"
                      className="absolute left-0 top-1/2 -translate-y-1/2 w-1.5 h-8 bg-accent-gradient rounded-r-md shadow-[0_0_10px_rgba(0,251,251,0.5)]"
                    />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-footer mt-auto p-6 bg-gradient-to-t from-[rgba(14,14,14,0.8)] to-transparent">
          <div className="flex items-center gap-3 p-3 rounded-xl bg-bg-glass border border-border-subtle cursor-pointer hover:border-accent-blue transition-colors">
            <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-violet-500 to-cyan-400 flex items-center justify-center text-white font-bold text-xs ring-2 ring-surface">
              AB
            </div>
            <div className="flex flex-col">
              <span className="text-xs font-bold text-text-primary">Portfolio Project</span>
              <span className="text-[0.65rem] text-text-muted">v2.0 • Advanced</span>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};
